/**
 * The visitor's cookie choice: may MaiTeams' own analytics run in this browser?
 *
 * One category decides it. "Analytics" covers Google Analytics, PostHog
 * (product analytics and session replay), New Relic's browser agent, Sentry's
 * performance tracing and profiling, and Dub's referral attribution when that
 * program is switched on. Everything else MaiTeams sets is essential: signing
 * in, security, remembering this choice, the theme, payments.
 *
 * Who is asked (owner decision 2026-09-28, "enterprise standard"):
 *   - Visitors in the EU, the EEA, the UK and Switzerland, judged from Vercel's
 *     `x-vercel-ip-country` header, see a banner, and nothing non-essential
 *     loads before they accept. No header at all is treated as "ask": the
 *     banner is the safe side of an unknown.
 *   - Everyone else gets analytics unless they turn it off (the footer's
 *     "Cookie settings").
 *   - Everywhere, a Global Privacy Control signal turns off what could count as
 *     a sale or share of personal information: Google Analytics (Google may use
 *     that data for its own purposes) and Dub (it reports conversions to a
 *     partner). PostHog, New Relic and Sentry process data only for MaiTeams,
 *     so GPC leaves them to the consent choice.
 *
 * Where it is kept: a visitor's choice lives in the `mt-cookie-consent` cookie
 * only. A signed-in user's choice also lives on the ACCOUNT, as one
 * `user_preferences` row (the `lib/preferences/color-mode.ts` shape), and the
 * cookie is its mirror; the newer decision wins in either direction, so a
 * anonymous choice made on the sign-in page is not overruled by an older one
 * on the account. Mirrors are bound to that account; legacy unowned cookies
 * are never copied into an account.
 *
 * Pure apart from the two database helpers, which take the caller's client.
 * Safe in the browser bundle.
 */
import type { SupabaseClient } from '@supabase/supabase-js'

export const COOKIE_CONSENT_COOKIE = 'mt-cookie-consent'
/**
 * Set by the consent endpoint, as a session cookie, only for a visitor OUTSIDE
 * the consent region, so later page loads in that browser session start
 * analytics without asking the server again. Never set for a visitor who must
 * be asked: nothing is stored on their device before they choose.
 */
export const CONSENT_REGION_COOKIE = 'mt-consent-region'
export const CONSENT_REGION_OPEN = 'open'

/** A decision is asked again after a year. */
export const COOKIE_CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365
const CHOICE_VERSION = 'v1'
const OWNED_CHOICE_VERSION = 'v2'

export type AnalyticsChoice = 'granted' | 'denied'

export interface CookieConsentChoice {
  analytics: AnalyticsChoice
  /** Epoch milliseconds of the decision. */
  decidedAt: number
  /** Cookie ownership: null is an anonymous choice; absent is a legacy, unowned mirror. */
  userId?: string | null
}

/**
 * ISO 3166-1 alpha-2 codes that see the banner: the 27 EU member states, the
 * EEA's Iceland, Liechtenstein and Norway, the UK and Switzerland. The EU's
 * outermost regions and Åland have their own codes and are EU territory, so
 * they are listed too.
 */
export const CONSENT_REGION_COUNTRIES: ReadonlySet<string> = new Set([
  // EU
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EU territory with its own code
  'AX', 'GF', 'GP', 'MQ', 'RE', 'YT', 'MF',
  // EEA
  'IS', 'LI', 'NO',
  // UK and Switzerland
  'GB', 'CH',
])

/** True when the visitor must be asked before analytics load. An absent or malformed country asks. */
export function consentRequiredForCountry(country: string | null | undefined): boolean {
  if (typeof country !== 'string') return true
  const code = country.trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(code)) return true
  // Vercel reports an unresolvable address as XX; ask.
  if (code === 'XX') return true
  return CONSENT_REGION_COUNTRIES.has(code)
}

function parseAnalyticsChoice(value: unknown): AnalyticsChoice | null {
  return value === 'granted' || value === 'denied' ? value : null
}

function parseDecidedAt(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null
}

/** A decision older than a year no longer counts; a future-dated one is refused. */
function isCurrent(decidedAt: number, now: number): boolean {
  return decidedAt <= now + 5 * 60_000 && now - decidedAt < COOKIE_CONSENT_MAX_AGE_SECONDS * 1000
}

/** Legacy `v1.granted.<epoch ms>`; owned `v2.granted.<epoch ms>.<user id or ->`. */
export function serializeCookieConsent(choice: CookieConsentChoice, userId?: string | null): string {
  if (userId !== undefined) return `${OWNED_CHOICE_VERSION}.${choice.analytics}.${choice.decidedAt}.${userId ?? '-'}`
  return `${CHOICE_VERSION}.${choice.analytics}.${choice.decidedAt}`
}

export function parseCookieConsent(raw: string | null | undefined, now: number = Date.now()): CookieConsentChoice | null {
  if (typeof raw !== 'string') return null
  const [version, analyticsRaw, decidedRaw, ...rest] = raw.trim().split('.')
  if (version === CHOICE_VERSION ? rest.length !== 0 : version !== OWNED_CHOICE_VERSION || rest.length !== 1) return null
  const owner = rest[0]
  if (version === OWNED_CHOICE_VERSION && owner !== '-' && !/^[a-zA-Z0-9_-]{1,128}$/.test(owner)) return null
  const analytics = parseAnalyticsChoice(analyticsRaw)
  const decidedAt = parseDecidedAt(decidedRaw)
  if (!analytics || decidedAt === null || !isCurrent(decidedAt, now)) return null
  return { analytics, decidedAt, ...(version === OWNED_CHOICE_VERSION ? { userId: owner === '-' ? null : owner } : {}) }
}

/** The account row's value, `{ analytics, decided_at }`. */
export function parseStoredCookieConsent(value: unknown, now: number = Date.now()): CookieConsentChoice | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  const analytics = parseAnalyticsChoice(record.analytics)
  const decidedAt = parseDecidedAt(record.decided_at)
  if (!analytics || decidedAt === null || !isCurrent(decidedAt, now)) return null
  return { analytics, decidedAt }
}

/** The newer of two decisions; the account wins a tie, so a mirror never overwrites its source. */
export function newerChoice(
  account: CookieConsentChoice | null,
  browser: CookieConsentChoice | null
): CookieConsentChoice | null {
  if (!account) return browser
  if (!browser) return account
  return browser.decidedAt > account.decidedAt ? browser : account
}

export interface TrackingPermissions {
  /** GA, PostHog, New Relic, Sentry performance, Dub. */
  analytics: boolean
  /** The subset that could count as a sale or share: GA and Dub. Off under GPC. */
  saleOrSharing: boolean
}

export interface ConsentInputs {
  /** False on a capability page, a customer's page or a white-label host: nothing of ours runs there. */
  surface: boolean
  choice: CookieConsentChoice | null
  /** null until known. Unknown is treated as "ask". */
  consentRequired: boolean | null
  gpc: boolean
}

export function resolveTrackingPermissions(input: ConsentInputs): TrackingPermissions {
  if (!input.surface) return { analytics: false, saleOrSharing: false }
  const analytics = input.choice ? input.choice.analytics === 'granted' : input.consentRequired === false
  return { analytics, saleOrSharing: analytics && !input.gpc }
}

/**
 * The browser's answer, worked out on the server from one request's own
 * signals: the `mt-cookie-consent` cookie (or a newer decision on the
 * account), the `Sec-GPC` header and Vercel's `x-vercel-ip-country`. Server
 * code that sends something about the visitor to an analytics or attribution
 * provider asks this, so it never does what the browser would refuse. The
 * surface is ours by construction: callers are MaiTeams' own routes.
 */
export function trackingPermissionsFromRequest(input: {
  cookieValue: string | null | undefined
  gpcHeader: string | null | undefined
  country: string | null | undefined
  account?: CookieConsentChoice | null
  /** The signed-in account. When given, the cookie counts only if it is anonymous or this account's own, as in the browser. */
  userId?: string
  now?: number
}): TrackingPermissions {
  const now = input.now ?? Date.now()
  const cookie = parseCookieConsent(input.cookieValue, now)
  const browser = input.userId !== undefined && cookie && cookie.userId !== null && cookie.userId !== input.userId ? null : cookie
  return resolveTrackingPermissions({
    surface: true,
    choice: newerChoice(input.account ?? null, browser),
    consentRequired: consentRequiredForCountry(input.country),
    gpc: input.gpcHeader?.trim() === '1',
  })
}

/** The banner shows only where our analytics could run, only to someone who must be asked, and only until they choose. */
export function shouldShowConsentBanner(input: ConsentInputs): boolean {
  return input.surface && input.choice === null && input.consentRequired !== false
}

/** Cookie attributes for the choice and its mirror. Not httpOnly: the browser reads it before anything loads. */
export function cookieConsentCookieOptions(): {
  path: string
  maxAge: number
  sameSite: 'lax'
  secure: boolean
  httpOnly: boolean
} {
  return {
    path: '/',
    maxAge: COOKIE_CONSENT_MAX_AGE_SECONDS,
    sameSite: 'lax',
    secure: process.env.NODE_ENV === 'production',
    httpOnly: false,
  }
}

/** Session cookie: gone when the browser closes, so a traveller is asked again in the next session. */
export function consentRegionCookieOptions(): {
  path: string
  sameSite: 'lax'
  secure: boolean
  httpOnly: boolean
} {
  return { path: '/', sameSite: 'lax', secure: process.env.NODE_ENV === 'production', httpOnly: false }
}

// ── The account half (user_preferences), the color-mode shape ──

const PREFERENCE_TYPE = 'privacy'
const PREFERENCE_KEY = 'cookie_consent'

/** The account's decision, or null when none (or none within the year). Throws on a database error. */
export async function readAccountCookieConsent(
  supabase: SupabaseClient,
  userId: string
): Promise<CookieConsentChoice | null> {
  // A later row write can carry an older decision (including in another org).
  // Page by the stable primary key; decision time, never write time, wins.
  const pageSize = 100
  let winner: CookieConsentChoice | null = null
  const now = Date.now()
  for (let offset = 0; ; offset += pageSize) {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('preference_value')
      .eq('user_id', userId)
      .eq('preference_type', PREFERENCE_TYPE)
      .eq('preference_key', PREFERENCE_KEY)
      .order('id', { ascending: true })
      .range(offset, offset + pageSize - 1)
    if (error) throw new Error(`cookie consent read failed: ${error.message}`)
    for (const row of data ?? []) winner = newerChoice(winner, parseStoredCookieConsent(row.preference_value, now))
    if (!data || data.length < pageSize) return winner
  }
}

/** Preserve the latest decision on the four-column key, using the caller's RLS client. */
export async function writeAccountCookieConsent(
  supabase: SupabaseClient,
  input: { userId: string; organizationId: string | null; choice: CookieConsentChoice }
): Promise<CookieConsentChoice> {
  const scoped = () => supabase.from('user_preferences')
  const key = {
    user_id: input.userId,
    preference_type: PREFERENCE_TYPE,
    preference_key: PREFERENCE_KEY,
  }
  const value = { analytics: input.choice.analytics, decided_at: input.choice.decidedAt }
  for (let attempt = 0; attempt < 8; attempt++) {
    let read = scoped().select('preference_value').match(key)
    read = input.organizationId === null ? read.is('organization_id', null) : read.eq('organization_id', input.organizationId)
    const { data, error } = await read.maybeSingle()
    if (error) throw new Error(`cookie consent read failed: ${error.message}`)
    const stored = parseStoredCookieConsent(data?.preference_value)
    if (stored && stored.decidedAt >= input.choice.decidedAt) return stored

    if (!data) {
      const inserted = await scoped().insert({
        user_id: input.userId,
        organization_id: input.organizationId,
        preference_type: PREFERENCE_TYPE,
        preference_key: PREFERENCE_KEY,
        preference_value: value,
        updated_at: new Date().toISOString(),
      }).select('preference_value').maybeSingle()
      // Another request inserted first. Read its decision before trying again.
      if (inserted.error?.code === '23505') continue
      if (inserted.error) throw new Error(`cookie consent write failed: ${inserted.error.message}`)
      if (inserted.data) return input.choice
    } else {
      let update = scoped().update({ preference_value: value, updated_at: new Date().toISOString() }).match(key)
      update = input.organizationId === null ? update.is('organization_id', null) : update.eq('organization_id', input.organizationId)
      // JSONB equality guards the entire value, including a malformed/expired row.
      update = data.preference_value === null
        ? update.is('preference_value', null)
        : update.eq('preference_value', JSON.stringify(data.preference_value))
      const updated = await update.select('preference_value').maybeSingle()
      if (updated.error) throw new Error(`cookie consent write failed: ${updated.error.message}`)
      if (updated.data) return input.choice
    }
  }
  throw new Error('cookie consent write contention')
}
